import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { endpoints } from '@/api/endpoints';
import { useT } from '@/i18n/LanguageContext';
import { LanguageToggle } from '@/i18n/LanguageToggle';
import { roleLabel } from '@/i18n/roleLabel';
import type { InvitationInfo, RecoveryInfo } from '@/api/types';
import { describeStaffError, takeTokenFromHash } from '@/pages/Users/staff';

/**
 * ADR-084 — the two one-time links, opened by the employee:
 *   /invite#<secret>   join the company: choose a password (or confirm an
 *                      existing one), then signed in;
 *   /recover#<secret>  set a new password after the administrator issued a
 *                      recovery link.
 * The secret is read from the fragment and removed from the address bar at
 * once. The page deliberately never says whether an email already has an
 * account: one password field, the server decides.
 */
export function AcceptLinkPage({ kind }: { kind: 'invite' | 'recover' }): JSX.Element {
  const t = useT();
  const { state, login } = useAuth();
  const token = useRef<string>('');
  const [info, setInfo] = useState<InvitationInfo | RecoveryInfo | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Read once: the fragment is cleared right after, and StrictMode runs effects twice.
    if (!token.current) token.current = takeTokenFromHash();
    if (!token.current) {
      setInfo({ state: 'INVALID' });
      return;
    }
    (kind === 'invite' ? endpoints.inspectInvitation(token.current) : endpoints.inspectRecovery(token.current))
      .then((r) => {
        setInfo(r);
        const dn = (r as InvitationInfo).displayName;
        if (dn) setName(dn);
      })
      .catch(() => setLoadError(true));
  }, [kind]);

  if (done && state.status === 'authenticated') return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (kind === 'recover' && password !== repeat) return;
    setBusy(true);
    setError(null);
    try {
      const r =
        kind === 'invite'
          ? await endpoints.acceptInvitation({ token: token.current, password, ...(name.trim() ? { displayName: name.trim() } : {}) })
          : await endpoints.completeRecovery({ token: token.current, password });
      setPassword('');
      setRepeat('');
      setDone(true);
      await login(r.sessionToken);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const invalid = info && info.state !== 'VALID';
  const inv = kind === 'invite' ? (info as InvitationInfo | null) : null;
  const errText = error ? describeStaffError(t, error) ?? t('staff.err.checkFields') : null;
  return (
    <main className="login-shell card">
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <LanguageToggle />
      </div>
      <h1 style={{ fontSize: 18, marginTop: 0 }}>{kind === 'invite' ? t('accept.inviteTitle') : t('accept.recoverTitle')}</h1>
      {!info && !loadError && <p className="sub">{t('common.loading')}</p>}
      {loadError && <div className="error-banner" role="alert">{t('staff.err.network')}</div>}
      {invalid && (
        <div className="notice" role="alert">
          {t(`accept.state.${info!.state}` as 'accept.state.EXPIRED')} <Link to="/login">{t('accept.toLogin')}</Link>
        </div>
      )}
      {info && info.state === 'VALID' && (
        <form onSubmit={(e) => void onSubmit(e)} className="wh-form">
          <dl className="kv">
            <dt>{t('accept.company')}</dt>
            <dd>{info.organizationName}</dd>
            <dt>{t('login.email')}</dt>
            <dd>{info.email}</dd>
            {inv?.role && (
              <>
                <dt>{t('settings.role')}</dt>
                <dd>{roleLabel(t, inv.role)}</dd>
                <dt>{t('settings.warehouse')}</dt>
                <dd>{inv.warehouseName ?? t('settings.orgWide')}</dd>
              </>
            )}
          </dl>
          {state.status === 'authenticated' && !done && <p className="notice">{t('accept.signedInWarning')}</p>}
          {kind === 'invite' && (
            <div className="field-row">
              <label htmlFor="accept-name">{t('accept.yourName')}</label>
              <input id="accept-name" value={name} maxLength={120} autoComplete="name" onChange={(e) => setName(e.target.value)} />
            </div>
          )}
          <div className="field-row">
            <label htmlFor="accept-password">{kind === 'invite' ? t('accept.password') : t('accept.newPassword')}</label>
            <input
              id="accept-password"
              type="password"
              autoComplete="new-password"
              value={password}
              minLength={kind === 'recover' ? 10 : undefined}
              onChange={(e) => setPassword(e.target.value)}
              required
              aria-describedby="accept-password-hint"
            />
            <p id="accept-password-hint" className="field-hint">
              {kind === 'invite' ? t('accept.passwordHintInvite') : t('accept.passwordHint')}
            </p>
          </div>
          {kind === 'recover' && (
            <div className="field-row">
              <label htmlFor="accept-repeat">{t('accept.repeat')}</label>
              <input id="accept-repeat" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required />
              {repeat && repeat !== password && <p className="field-hint warn">{t('accept.mismatch')}</p>}
            </div>
          )}
          {errText && <div className="error-banner" role="alert">{errText}</div>}
          <div className="form-actions">
            <button type="submit" className="primary" disabled={busy || !password || (kind === 'recover' && password !== repeat)}>
              {busy ? t('login.signingIn') : kind === 'invite' ? t('accept.join') : t('accept.save')}
            </button>
          </div>
        </form>
      )}
    </main>
  );
}
